import axios from '@axios'

export default {
  namespaced: true,
  state: {
    credit: null,
    loading: false,
    lastFetched: null,
    contactUrl: null,
    // Mock credit state for free promo users
    isMockCredit: false,
    mockCreditAmount: 0,
    isDeposit: true,
    isFreePromo: false,
    promoMinimumDeposit: 0,
    // Game restriction of the member's active promotion (from member/check-restrict)
    gameRestriction: { mode: 'none', providers: [], games: [] },
    gameRestrictionFetched: null
  },
  getters: {
    balance: state => state.credit?.result?.credit ?? 0,
    diamondBalance: state => state.credit?.result?.diamond_balance ?? 0,
    creditData: state => state.credit,
    isLoading: state => state.loading,
    contactUrl: state => state.contactUrl,
    isMockCredit: state => state.isMockCredit,
    promoMinimumDeposit: state => state.promoMinimumDeposit,
    displayBalance: state => {
      if (state.isMockCredit) {
        return state.mockCreditAmount
      }
      return state.credit?.result?.credit ?? 0
    },
    // Same provider+game_id matching as the backend PromotionRestrictionService
    isGameBlocked: state => (provider, gameId) => {
      const restriction = state.gameRestriction
      if (!restriction || restriction.mode === 'none') return false
      const p = String(provider || '').toUpperCase()
      const g = String(gameId || '')
      const hit =
        (restriction.providers || []).some(
          x => String(x).toUpperCase() === p
        ) ||
        (restriction.games || []).some(
          x =>
            String(x.provider || '').toUpperCase() === p &&
            String(x.game_id) === g
        )
      return restriction.mode === 'block' ? hit : !hit
    }
  },
  mutations: {
    SET_CREDIT(state, credit) {
      state.credit = credit
      state.lastFetched = Date.now()
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    CLEAR_CREDIT(state) {
      state.credit = null
      state.lastFetched = null
    },
    SET_CONTACT_URL(state, url) {
      state.contactUrl = url
    },
    SET_MOCK_CREDIT(
      state,
      { isMockCredit, mockCreditAmount, isDeposit, isFreePromo, minimumDeposit }
    ) {
      state.isMockCredit = isMockCredit
      state.mockCreditAmount = mockCreditAmount
      state.isDeposit = isDeposit
      state.isFreePromo = isFreePromo
      state.promoMinimumDeposit = minimumDeposit || 0
    },
    SET_GAME_RESTRICTION(state, restriction) {
      state.gameRestriction = restriction || {
        mode: 'none',
        providers: [],
        games: []
      }
      state.gameRestrictionFetched = Date.now()
    }
  },
  actions: {
    async fetchCredit({ commit, state }, { force = false } = {}) {
      // Skip if recently fetched (within 5 seconds) unless forced
      if (
        !force &&
        state.lastFetched &&
        Date.now() - state.lastFetched < 5000
      ) {
        return state.credit
      }

      commit('SET_LOADING', true)
      try {
        const resp = await axios.get('/member/credit')
        commit('SET_CREDIT', resp.data)
        return resp.data
      } catch (error) {
        console.error('Error fetching credit:', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    clearCredit({ commit }) {
      commit('CLEAR_CREDIT')
    },
    async fetchContactUrl({ commit, state }) {
      if (state.contactUrl) return state.contactUrl
      try {
        const resp = await axios.get('/member/contact')
        if (resp.data.success && resp.data.url) {
          commit('SET_CONTACT_URL', resp.data.url)
          return resp.data.url
        }
      } catch (error) {
        console.error('Error fetching contact URL:', error)
      }
      return null
    },
    async fetchGameRestriction({ commit, state }, { force = false } = {}) {
      // Skip if recently fetched (within 30 seconds) unless forced
      if (
        !force &&
        state.gameRestrictionFetched &&
        Date.now() - state.gameRestrictionFetched < 30000
      ) {
        return state.gameRestriction
      }
      try {
        const resp = await axios.get('/member/check-restrict')
        const restriction = resp.data?.restriction || {
          mode: 'none',
          providers: [],
          games: []
        }
        commit('SET_GAME_RESTRICTION', restriction)
        return restriction
      } catch (error) {
        console.error('Error fetching game restriction:', error)
        // Fail-open: never block games on a fetch error
        commit('SET_GAME_RESTRICTION', {
          mode: 'none',
          providers: [],
          games: []
        })
        return state.gameRestriction
      }
    },
    async checkFreePromo({ commit }) {
      try {
        const resp = await axios.post('/member/check/free-promo')
        const { isDeposit, isFreePromo, amount, minimumDeposit } = resp.data

        const isMock = !isDeposit && isFreePromo
        commit('SET_MOCK_CREDIT', {
          isMockCredit: isMock,
          mockCreditAmount: amount,
          isDeposit,
          isFreePromo,
          minimumDeposit
        })
        return { isMockCredit: isMock, amount, minimumDeposit }
      } catch (error) {
        console.error('Error checking free promo:', error)
        // Default to real credit on error
        commit('SET_MOCK_CREDIT', {
          isMockCredit: false,
          mockCreditAmount: 0,
          isDeposit: true,
          isFreePromo: false,
          minimumDeposit: 0
        })
        return { isMockCredit: false, amount: 0, minimumDeposit: 0 }
      }
    }
  }
}
